import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { selectLivePositions } from '@/features/domain/device'
import { gpsReverseGeocode } from '@/features/domain/ui'
import { useAppDispatch } from '@/store'
import { useIsUnmounted } from '@/hooks'
import { useGetMapInfo } from '@/map'

export function useLastPosition(device: uui.domain.client.gps.wwgps.DeviceInfo) {
  const dispatch = useAppDispatch()
  const { bounds } = useGetMapInfo()
  const isUnmounted = useIsUnmounted()
  const livePositions = useSelector(selectLivePositions)

  const lastPosition = livePositions[device.deviceId]

  const [location, setLocation] = useState<
    | uui.domain.client.gps.wwgps.GpsInfo
    | uui.domain.client.gps.telematics.CurrentPositionGpsInfo
    | 'loading'
    | undefined
  >('loading')

  const boundsRef = useRef<uui.domain.LatLngBounds>(bounds)
  useEffect(() => {
    boundsRef.current = bounds
  }, [bounds])

  useEffect(() => {
    async function reverseGeocodeLastPositions(
      lastPosition:
        | uui.domain.client.gps.wwgps.GpsInfo
        | uui.domain.client.gps.telematics.CurrentPositionGpsInfo,
    ) {
      setLocation('loading')

      const response = await dispatch(
        gpsReverseGeocode({ latLng: lastPosition.latLng, bounds: boundsRef.current }),
      )

      if (gpsReverseGeocode.rejected.match(response)) {
        throw new Error('Request failed')
      }

      if (isUnmounted()) return

      setLocation({ ...lastPosition, address: response.payload.geoAddress })
    }

    if (lastPosition) {
      if (lastPosition.address) {
        setLocation(lastPosition)
        return
      }

      reverseGeocodeLastPositions(lastPosition)
    } else {
      setLocation(undefined)
    }
  }, [dispatch, isUnmounted, lastPosition])

  return location
}
